import { useState, useEffect, useRef } from "react";

const testimonials = [
  {
    quote: (
      <>
        IPDI didn't just help me get a job — it completely
        <em> repositioned</em> my career. Within 6 weeks of completing the
        BIM program, I had three offers from firms I'd only dreamed of
        joining.
      </>
    ),
    initials: "AP",
    name: "Ananya Patel",
    role: "BIM Coordinator · HOK India · IPDI Batch '23",
  },
  {
    quote: (
      <>
        We hired 14 professionals through IPDI this year. The quality is
        unmatched — candidates come
        <em> trained, tested, and ready.</em> It's transformed how we
        recruit.
      </>
    ),
    initials: "RS",
    name: "Rohit Sharma",
    role: "Principal Architect · Studio Lotus · Partner Since 2022",
  },
  {
    quote: (
      <>
        Our college partnership with IPDI improved our placement rate from
        61% to <em>94% in one year.</em> The industry network they bring to
        our students is extraordinary.
      </>
    ),
    initials: "NK",
    name: "Dr. Neha Kapoor",
    role: "Dean, School of Architecture · Symbiosis International",
  },
];

const AUTOPLAY_DELAY = 4000;
const extendedTestimonials = [...testimonials, testimonials[0]]; // clone of first slide at the end

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const isPausedRef = useRef(false);
  const trackRef = useRef(null);

  // Autoplay interval — clamps index so it can NEVER go past the
  // extendedTestimonials array, even if transitionend gets missed
  // (e.g. section hidden, fast unmount/remount on route change etc.)
  useEffect(() => {
    const interval = setInterval(() => {
      if (isPausedRef.current) return;
      setActiveIndex((prev) => {
        const next = prev + 1;
        return next >= extendedTestimonials.length ? 0 : next;
      });
    }, AUTOPLAY_DELAY);

    return () => clearInterval(interval);
  }, []);

  // Pause autoplay when tab/section is not visible, so transitionend
  // doesn't get missed while hidden (main cause of "blank on return" bug)
  useEffect(() => {
    const handleVisibility = () => {
      isPausedRef.current = document.hidden;
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleTransitionEnd = () => {
    // if we've slid onto the cloned first slide, snap back to real index 0 without animating
    if (activeIndex === testimonials.length) {
      setTransitionEnabled(false);
      setActiveIndex(0);
    }
  };

  useEffect(() => {
    if (!transitionEnabled) {
      // re-enable transition on next frame after the instant snap
      const raf = requestAnimationFrame(() => setTransitionEnabled(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [transitionEnabled]);

  // Extra safety: whenever activeIndex changes, if it somehow ends up
  // out of bounds (>= extendedTestimonials.length), clamp it back instantly.
  useEffect(() => {
    if (activeIndex >= extendedTestimonials.length) {
      setTransitionEnabled(false);
      setActiveIndex(0);
    }
  }, [activeIndex]);

  const goToSlide = (index) => {
    setTransitionEnabled(true);
    setActiveIndex(index);
  };

  const activeDotIndex = activeIndex % testimonials.length;

  return (
    <section
      className="its-section"
      onMouseEnter={() => (isPausedRef.current = true)}
      onMouseLeave={() => (isPausedRef.current = false)}
    >
      <div className="its-container">
        <div className="its-eyebrow">
          <span className="its-eyebrow-line" />
          <span className="its-eyebrow-text">WHAT OUR COMMUNITY SAYS</span>
        </div>

        <h2 className="its-heading">
          Real People. <em>Real Careers.</em>
        </h2>

        <div className="its-stage">
          <div
            ref={trackRef}
            className="its-track"
            onTransitionEnd={handleTransitionEnd}
            style={{
              transform: `translateX(-${activeIndex * 100}%)`,
              transition: transitionEnabled ? "transform 0.7s ease" : "none",
            }}
          >
            {extendedTestimonials.map((t, index) => (
              <div key={index} className="its-card">
                <p className="its-quote">{t.quote}</p>

                <div className="its-author">
                  <div className="its-avatar">{t.initials}</div>
                  <div className="its-author-info">
                    <span className="its-name">{t.name}</span>
                    <span className="its-role">{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="its-dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`its-dot ${
                index === activeDotIndex ? "its-dot-active" : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        .its-section {
          background: transparent;
          padding: 80px 24px;
        }

        .its-container {
          max-width: 860px;
          margin: 0 auto;
          text-align: center;
        }

        .its-eyebrow {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 18px;
        }

        .its-eyebrow-line {
          width: 28px;
          height: 1px;
          background: #c84b20;
        }

        .its-eyebrow-text {
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.14em;
          color: #c84b20;
          font-family: "Space Mono",monospace;
        }

        .its-heading {
          font-family: "Playfair Display", Georgia, serif;
          font-size: 47px;
          font-weight: 400;
          color: #1a1a1a;
          margin: 0 0 56px 0;
          line-height: 1.2;
        }

        .its-heading em {
          font-style: italic;
          color: #c84b20;
        }

        .its-stage {
          position: relative;
          overflow: hidden;
        }

        .its-track {
          display: flex;
        }

        .its-card {
          flex: 0 0 100%;
          min-width: 100%;
          box-sizing: border-box;
          padding: 0 4px;
        }

        .its-quote {
          font-family: "Playfair Display", Georgia, serif;
          font-style: italic;
          font-size: 21px;
          line-height: 1.5;
          color: #2a2a2a;
          margin: 0 0 32px 0;
        }

        .its-quote em {
          font-style: italic;
          color: #c84b20;
        }

        .its-author {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
        }

        .its-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #c84b20;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .its-author-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .its-name {
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .its-role {
          font-size: 13px;
          color: #777;
          text-align: start;
        }

        .its-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 40px;
        }

        .its-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: none;
          background: #d9d0c9;
          cursor: pointer;
          padding: 0;
          transition: background 0.3s ease, transform 0.3s ease;
        }

        .its-dot-active {
          background: #c84b20;
          transform: scale(1.2);
        }

        @media (max-width: 640px) {
          .its-heading {
            font-size: 30px;
          }

          .its-quote {
            font-size: 19px;
          }

        }
      `}</style>
    </section>
  );
}